const QUICK_VIEW_TEMPLATE = 'quick-view';

const QUICK_VIEW_DEFAULT_STRINGS = {
  title: 'Snel bekijken',
  loading: 'Laden…',
  error: 'Laden lukt niet.',
  retry: 'Opnieuw proberen',
  viewProduct: 'Bekijk productpagina',
  close: 'Sluiten',
};

const QUICK_VIEW_DIALOG_CLASS = [
  'fixed inset-x-0 top-auto bottom-0 h-auto w-full max-h-[90dvh] overflow-y-auto overscroll-contain rounded-t-3xl bg-canvas text-ink shadow-lift',
  'translate-y-full open:translate-y-0 starting:open:translate-y-full',
  'md:inset-0 md:m-auto md:h-fit md:max-h-[min(90dvh,48rem)] md:w-[min(56rem,calc(100vw-3rem))] md:rounded-3xl',
  'md:translate-y-4 md:opacity-0 md:open:translate-y-0 md:open:opacity-100 md:starting:open:translate-y-4 md:starting:open:opacity-0',
  'motion-safe:transition-[translate,opacity,display,overlay] motion-safe:duration-300 motion-safe:ease-out-soft motion-safe:transition-discrete',
  'backdrop:bg-ink/40 backdrop:backdrop-blur-[2px]',
].join(' ');

const QUICK_VIEW_PANEL_CLASS = 'grid gap-6 p-5 pb-[max(1rem,env(safe-area-inset-bottom))] md:grid-cols-2 md:gap-8 md:p-8';

const QUICK_VIEW_SHELL = `
  <div class="sticky top-0 z-10 flex h-0 justify-end pointer-events-none">
    <button type="button" class="btn btn-plain btn-icon btn-sm pointer-events-auto mt-2 mr-2 bg-canvas/90 backdrop-blur text-ink" data-quick-view-close autofocus>
      <svg class="shrink-0 size-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18M6 6l12 12"/></svg>
    </button>
  </div>
  <div data-quick-view-body></div>
`;

const QUICK_VIEW_SKELETON = `
  <div class="${QUICK_VIEW_PANEL_CLASS}">
    <p class="sr-only" role="status" data-quick-view-status></p>
    <div class="aspect-square animate-pulse rounded-2xl bg-surface" aria-hidden="true"></div>
    <div class="grid content-start gap-4" aria-hidden="true">
      <div class="h-4 w-1/3 animate-pulse rounded-full bg-surface"></div>
      <div class="h-6 w-2/3 animate-pulse rounded-full bg-surface"></div>
      <div class="h-6 w-1/3 animate-pulse rounded-full bg-surface"></div>
      <div class="h-4 w-1/2 animate-pulse rounded-full bg-surface"></div>
      <div class="h-11 w-full animate-pulse rounded-full bg-surface"></div>
      <div class="h-11 w-full animate-pulse rounded-full bg-surface"></div>
    </div>
  </div>
`;

const QUICK_VIEW_ERROR = `
  <div class="grid place-items-center gap-4 p-8 pb-[max(1rem,env(safe-area-inset-bottom))] text-center" role="alert">
    <p class="font-display text-lead font-bold" data-quick-view-error-text></p>
    <div class="flex flex-wrap justify-center gap-3">
      <button type="button" class="btn btn-primary btn-sm" data-quick-view-retry></button>
      <a class="btn btn-ghost btn-sm" data-quick-view-product-link></a>
    </div>
  </div>
`;

const quickViewStrings = () => ({ ...QUICK_VIEW_DEFAULT_STRINGS, ...window.theme?.strings?.quickView });

const toSameOriginUrl = (value) => {
  try {
    const url = new URL(value, window.location.origin);
    return url.origin === window.location.origin ? url : null;
  } catch {
    return null;
  }
};

class QuickViewDialog extends HTMLElement {
  static get instance() {
    let element = document.querySelector('quick-view-dialog');
    if (!element) {
      element = document.createElement('quick-view-dialog');
      document.body.append(element);
    }
    return element;
  }

  connectedCallback() {
    if (this.dialog) return;
    this.dialog = document.createElement('dialog');
    this.dialog.className = QUICK_VIEW_DIALOG_CLASS;
    this.dialog.innerHTML = QUICK_VIEW_SHELL;
    this.dialog.querySelector('[data-quick-view-close]').setAttribute('aria-label', quickViewStrings().close);
    this.body = this.dialog.querySelector('[data-quick-view-body]');
    this.append(this.dialog);

    this.dialog.addEventListener('click', this.onClick.bind(this));
    this.dialog.addEventListener('change', this.onChange.bind(this));
    this.dialog.addEventListener('close', this.onClose.bind(this));
  }

  open(url, trigger) {
    this.url = url;
    this.trigger = trigger;
    this.closedForCart = false;
    if (!this.dialog.open) this.dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    this.watchCartDrawer();
    this.load();
  }

  async load() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.showLoading();

    const requestUrl = new URL(this.url);
    requestUrl.searchParams.set('view', QUICK_VIEW_TEMPLATE);

    try {
      const response = await fetch(requestUrl, { signal: this.controller.signal });
      if (!response.ok) throw new Error(`Quick view request failed: ${response.status}`);
      const content = parseHTML(await response.text()).querySelector('[data-quick-view-content]');
      if (!content) throw new Error('Quick view content missing');
      this.showContent(content);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error(error);
      this.showError();
    }
  }

  showLoading() {
    const strings = quickViewStrings();
    this.body.innerHTML = QUICK_VIEW_SKELETON;
    this.body.querySelector('[data-quick-view-status]').textContent = strings.loading;
    this.dialog.removeAttribute('aria-labelledby');
    this.dialog.setAttribute('aria-label', strings.title);
    this.dialog.setAttribute('aria-busy', 'true');
  }

  showContent(content) {
    this.variants = this.readVariants(content);
    this.body.replaceChildren(content);
    this.dialog.removeAttribute('aria-busy');
    if (content.dataset.titleId) {
      this.dialog.removeAttribute('aria-label');
      this.dialog.setAttribute('aria-labelledby', content.dataset.titleId);
    }
  }

  showError() {
    const strings = quickViewStrings();
    this.body.innerHTML = QUICK_VIEW_ERROR;
    this.body.querySelector('[data-quick-view-error-text]').textContent = strings.error;
    this.body.querySelector('[data-quick-view-retry]').textContent = strings.retry;
    const link = this.body.querySelector('[data-quick-view-product-link]');
    link.textContent = strings.viewProduct;
    link.href = this.url.href;
    this.dialog.removeAttribute('aria-busy');
  }

  readVariants(content) {
    try {
      return JSON.parse(content.querySelector('[data-quick-view-variants]')?.textContent || '[]');
    } catch {
      return [];
    }
  }

  onClick(event) {
    if (event.target.closest('[data-quick-view-close]')) {
      this.dialog.close();
      return;
    }
    if (event.target.closest('[data-quick-view-retry]')) {
      this.load();
      this.dialog.querySelector('[data-quick-view-close]').focus();
      return;
    }
    if (event.target === this.dialog && this.isOutsidePanel(event)) this.dialog.close();
  }

  isOutsidePanel(event) {
    const rect = this.dialog.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
  }

  onChange(event) {
    const select = event.target.closest('[data-quick-view-variant]');
    if (!select) return;
    const variant = this.variants?.find((item) => String(item.id) === select.value);
    if (!variant) return;

    const price = this.body.querySelector('[data-quick-view-price]');
    if (price) price.innerHTML = variant.price_html;

    const form = select.form;
    const button = form?.querySelector('[type="submit"]');
    if (button) button.disabled = !variant.available;
    const label = form?.querySelector('[data-quick-view-add-label]');
    if (label) label.textContent = variant.available ? window.theme.strings.addToCart : window.theme.strings.soldOut;
    form?.closest('product-form')?.querySelector('[data-form-error]')?.setAttribute('hidden', '');
  }

  watchCartDrawer() {
    const drawerDialog = document.querySelector('cart-drawer dialog');
    if (!drawerDialog || this.cartObserver) return;
    this.cartObserver = new MutationObserver(() => {
      if (!drawerDialog.open || !this.dialog.open) return;
      this.closedForCart = true;
      this.dialog.close();
    });
    this.cartObserver.observe(drawerDialog, { attributes: true, attributeFilter: ['open'] });
  }

  onClose() {
    this.controller?.abort();
    this.cartObserver?.disconnect();
    this.cartObserver = null;
    if (!document.querySelector('dialog[open]')) document.documentElement.style.overflow = '';

    if (this.closedForCart) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.returnFocus = this.trigger;
      return;
    }
    this.trigger?.focus?.();
  }
}
customElements.define('quick-view-dialog', QuickViewDialog);

document.addEventListener('click', (event) => {
  const trigger = event.target.closest('[data-quick-view]');
  if (!trigger) return;
  const url = toSameOriginUrl(trigger.dataset.quickView);
  if (!url) return;
  event.preventDefault();
  QuickViewDialog.instance.open(url, trigger);
});
